import { CalendarBlockEventStatusEnum, CommandIndexProvidersQuery, ShiftStatusEnum, VisitEventStatusEnum } from "~/graphql/__generated__/graphql";
import { LocalCalendarBlockBase, LocalShiftBase, LocalVisitResourceBase } from "~/hooks/visitDragDropReducer";
import { visitResourceStatus } from "~/utils/statuses";

export type FilterState = {
  serviceLineIds: number[];
  showMyVisits: boolean;
  showCanceledVisits: boolean;
  showAllProviders: boolean;
  showAllPatients: boolean;
  showCanceledShifts: boolean;
}

export const filterVisits = (
  localVisitResources: LocalVisitResourceBase,
  filters: FilterState,
  currentProviderId: number | null,
) => {
  // Note: This function intentionally does not filter by serviceLineIds; the decision was that if a provider has
  // *any* visits for a service line, display all *all* visits for that provider; thus service line filtering lives
  // entirely at the provider level.
  const isCurrentProvider = (vr: typeof localVisitResources[number]) => vr.providerId === currentProviderId
  const visitHasCurrentProvider = (visitId: number) => localVisitResources.find(vr => vr.visitId === visitId && isCurrentProvider(vr));
  // Use utility function to generate base events from existing visits
  return localVisitResources
    .filter((vr) => filters.showCanceledVisits || visitResourceStatus(vr) !== VisitEventStatusEnum.Canceled)
    .filter((vr) => !filters.showMyVisits || isCurrentProvider(vr) || visitHasCurrentProvider(vr.visitId))
}

export const filterShifts = (
  visibleVisits: LocalVisitResourceBase,
  localShifts: LocalShiftBase,
  filters: FilterState,
  currentProviderId?: number | null,
) => {
  return localShifts
    .filter(s => filters.showCanceledShifts || s.status !== ShiftStatusEnum["Canceled"])
    // If we're only showing the current provider's visits, hide any irrelevant shifts
    .filter(s => !filters.showMyVisits || s.provider?.id === currentProviderId || visibleVisits.some(vr => vr.id === s.providerId))
}

export const filterCalendarBlocks = (
  calendarBlocks: LocalCalendarBlockBase,
  filters: FilterState,
  currentProviderId?: number | null,
) => {
  return calendarBlocks
    .filter(b => !filters.showMyVisits || b.providerId == currentProviderId)
    .filter(b => filters.showCanceledShifts || b.eventStatus === CalendarBlockEventStatusEnum.Scheduled)
}

export const filterProviders = (
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"],
  visibleShifts: LocalShiftBase,
  visibleVisits: LocalVisitResourceBase,
  visibleCalendarBlocks: LocalCalendarBlockBase,
  filters: FilterState,
  currentProviderId: number | null,
) => {
  const isVisibleServiceLine = (serviceLineId: number | undefined) => {
    return !serviceLineId || !filters.serviceLineIds.length || filters.serviceLineIds.includes(serviceLineId)
  }

  const providerMatchesServiceLineFilter = (provider: typeof providers[number]) => (
    // A provider matches the service line filter if we're not filtering by service lines,
    !filters.serviceLineIds.length ||
    // Or they belond to a service line we're showing
    provider.serviceLineProviders.some(slp => isVisibleServiceLine(slp.serviceLineId))
  );

  return providers.filter(provider =>
    // Providers are visible if they're active and either we're showing all of them or they are the current provider
    (provider.active && (filters.showAllProviders || provider.id === currentProviderId)) ||
    // or if they have a visible shift or calendar block.
    visibleShifts.some(s => s.providerId === provider.id) ||
    visibleCalendarBlocks.some(cbe => cbe.providerId === provider.id) ||
    // or at least one of their visits is visible and matches a displayed service line
    visibleVisits.some(v => v.providerId === provider.id && isVisibleServiceLine(v.visit.episode?.serviceLineId))
  ).filter(providerMatchesServiceLineFilter);
}
